<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { trackAddToCart } from "components/pod/pod-build-order/utils/ga_tracker";

const props = defineProps({
  id: {
    type: Number,
    required: true
  },
  size: {
    type: String,
    required: true
  },
  value: {
    type: Number,
    default: null
  },
  product: {
    type: Object,
    required: true
  },
  composition: {
    type: Object,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  }
});
const store = useStore();
const emit = defineEmits(["onChange"]);
const NUMBER_REGEX = /^\d+$/;

const variantPrice = computed(() =>
  store.getters["product/variantPrice"](props.composition.id, props.id)
);
const decrementDisabled = computed(() => !props.value);
const inputStyles = computed(() => ({
  "size-input__input": true,
  "size-input__input--selected": Number(props.value || 0) > 0,
  "size-input__input--disabled": props.disabled
}));
const visibleValue = computed(() => (props.disabled ? "" : props.value));
const visiblePlaceholder = computed(() => (props.disabled ? "Out of stock" : "0"));

const trackQuantityChange = (newQuantity, prevQuantity) => {
  if (newQuantity > prevQuantity) {
    const { product, composition } = props;
    trackAddToCart({
      product,
      composition,
      quantity: newQuantity,
      price: variantPrice.value,
      variantId: props.id
    });
  }
};

const onChange = value => {
  emit("onChange", { quantity: Number(value), id: props.id });
  trackQuantityChange(value, props.value);
};
const increment = () => {
  onChange(props.value + 1);
};
const decrement = () => {
  onChange(Math.max(props.value - 1, 0));
};
const onInputChange = event => {
  const { value } = event.target;
  if (NUMBER_REGEX.test(value) || value === "") {
    onChange(value);
  } else {
    // eslint-disable-next-line no-param-reassign
    event.target.value = props.value;
  }
};
</script>

<template>
  <div class="size-input">
    <div class="size-input__size">{{ size }}</div>
    <div class="size-input__form-group">
      <button
        v-if="!props.disabled"
        class="size-input__button"
        type="button"
        :disabled="decrementDisabled"
        @click="decrement"
      >
        <UseSvg icon="minus" class="size-input__button-icon" />
      </button>
      <input
        type="text"
        maxlength="4"
        :value="visibleValue"
        :class="inputStyles"
        :placeholder="visiblePlaceholder"
        :disabled="props.disabled"
        @input="onInputChange"
      />
      <button
        v-if="!props.disabled"
        class="size-input__button size-input__button--increment"
        type="button"
        @click="increment"
      >
        <UseSvg icon="plus" class="size-input__button-icon" />
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/pod/settings";
@import "frontend/init/jiffy/mixin";

.size-input {
  width: 100%;

  &__size {
    text-align: center;
    font-size: 14px;
    font-weight: 500;
    color: $text-black;
  }

  &__form-group {
    display: flex;
    position: relative;
    height: 36px;
  }

  &__input {
    width: 100%;
    padding: 1px 28px;
    text-align: center;
    appearance: none;
    border-radius: 4px;
    border: 2px solid $digital-assets-gray-modern-200;
    color: $crafterpay-gray-modern-900;
    font-size: 16px;
    font-weight: 600;
    outline: none;

    &:focus,
    &:focus-within,
    &:active {
      border-color: $pod-gray-modern-800;
    }

    &--selected {
      border-color: $pod-gray-modern-800;
    }

    &--disabled {
      padding: 1px 10px;
      font-size: 12px;

      @include with-tablet {
        font-size: 14px;
      }
    }
  }

  &__button {
    border: 0;
    outline: 0;
    background: none;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;

    &--increment {
      left: unset;
      right: 0;
    }
  }

  &__button-icon {
    width: 16px;
    height: 16px;
  }
}
</style>
